import classNames from 'classnames';
import { autobind } from 'core-decorators';

import PropTypes from 'prop-types';
import { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Icon from '/@/components/icon';
import OverlayReference from '/@/components/map/overlay-reference';
import settings from '/@/core/settings';
import { _ } from '/@/locale';
import { setVersionLabel } from '/@/store/maps/actions';
import { getVersionLabel } from '/@/store/maps/selectors';
import { trackEvent } from '/@/store/nav/actions';
import styles from './styles.module.css';

/** @extends Component<{icon: string, label: string, children: any[], expanded: boolean, selected: boolean, type: string}, {version?:string, expanded?: boolean}> */
@connect(
    (state) => ({}),
    (dispatch) => ({ ...bindActionCreators({ trackEvent, setVersionLabel }, dispatch), dispatch })
)
@autobind
class Item extends Component {
    static propTypes = {
        icon: PropTypes.string,
        label: PropTypes.string,
        children: PropTypes.array,
        expanded: PropTypes.bool,
        selected: PropTypes.bool,
        type: PropTypes.string,
    };

    state = {
        version: undefined,
        expanded: Boolean(this.props.expanded),
    };

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (nextProps.expanded !== this.props.expanded) this.setState({ expanded: nextProps.expanded });
    }

    handleClick(event) {
        event.stopPropagation();
        if (this.props.children && this.props.children.length) {
            this.setState((state) => ({ expanded: !state.expanded }));
        }
        if (this.props.onClick) {
            if (this.props.type === 'spodoptera_forecast') {
                this.props.trackEvent({ key: 'maps.spodoptera_forecast', type: 'sidebar' });
            }
            return this.props.onClick();
        }
    }

    handleOverlayChange({ version }) {
        if (this.props.overlay) {
            const newVersion = this.props.overlay.versions.find(({ versionNumber }) => versionNumber === version);
            this.setState({
                version: newVersion,
            });

            if (newVersion) this.props.setVersionLabel(_(newVersion.label));
        }
    }

    render() {
        const { expanded, version } = this.state;
        const { id, level, icon, children, selected, field, overlay } = this.props;
        const label = _(this.props.label);

        let fieldMapValuesId;
        let selectedVersion;
        if (field && !children) {
            fieldMapValuesId = field.mapValues.map(({ id }) => id);
            selectedVersion =
                version ||
                overlay.versions.filter((ov) => {
                    return ov.referenceItems.find((ri) => fieldMapValuesId.find((fmv) => fmv == ri.id));
                })[0];
        }
        return (
            <div
                key={id}
                onClick={this.handleClick}
                className={classNames(styles.item, styles[`level-${level}`], {
                    [styles.expanded]: expanded,
                    [styles.selected]: selected,
                })}
            >
                <div
                    className={classNames(styles.content, {
                        [styles.leaf]: !children,
                        [styles.bordered]: !!field,
                    })}
                >
                    {this.props.icon && (
                        <div className={styles.icon}>
                            <Icon type={icon} size='sm' theme={settings.app.theme} container='circle' />
                        </div>
                    )}
                    {(!field || (children && !!children.length)) && <div className={styles.label}>{_(label)}</div>}
                    {field && !children && (
                        <OverlayReference
                            overlay={overlay}
                            values={fieldMapValuesId}
                            version={selectedVersion}
                            onOverlayChange={this.handleOverlayChange}
                        />
                    )}
                    {children && !!children.length && (
                        <i className='material-icons'>{expanded ? 'expand_less' : 'expand_more'}</i>
                    )}
                </div>
                {children && !!children.length && expanded && (
                    <div className={styles.children}>
                        {children.map(({ id, ...item }) => (
                            <Item key={id} field={field} {...item} />
                        ))}
                    </div>
                )}
            </div>
        );
    }
}

@connect(
    (state) => ({
        versionLabel: getVersionLabel(state),
    }),
    (dispatch) => ({ ...bindActionCreators({ setVersionLabel }, dispatch), dispatch })
)
@autobind
export default class OverlayList extends Component {
    static propTypes = {
        selected: PropTypes.object,
        field: PropTypes.object,
        overlays: PropTypes.array,
        squashSingle: PropTypes.bool,
        onOverlayClick: PropTypes.func,
    };

    static defaultProps = {
        onOverlayClick: () => null,
    };

    validateOverlay(overlay) {
        if (!this.props.field) return true;
        const { field } = this.props;

        return overlay.versions
            .flatMap(({ referenceItems }) => referenceItems)
            .some(({ id: refId }) => field.mapValues.find(({ id }) => id === refId));
    }

    buildTree(items, item, level) {
        if (!item.type && !item.children) throw new Error('Invalid overlay items configuration.');

        const { squashSingle, selected = { overlay: {} } } = this.props;

        item.id = `${_(item.label)}-${level}`; // Used as key for components

        item.level = level;
        item.children = item.children
            ? item.children.reduce((children, child) => this.buildTree(children, child, level + 1), [])
            : [];

        let overlays = [];

        if (item.type) {
            overlays = this.props.overlays.filter(
                (overlay) => overlay.type === item.type && this.validateOverlay(overlay)
            );
        }

        if (!squashSingle || overlays.length !== 1) {
            // Overlay List
            item.children.push(
                ...overlays
                    .sort((a, b) => a.order - b.order)
                    .map((overlay) => {
                        const current = selected && selected.overlay.slug == overlay.slug;
                        return {
                            id: `${_(overlay.slug)}-${level}`,
                            level: level + 1,
                            label: _(overlay.name),
                            selected: current,
                            onClick: this.handleOverlayClick(!current ? overlay : undefined),
                            overlay: overlay,
                        };
                    })
            );
        } else {
            // Direct Link
            item.selected = selected && selected.overlay.slug == overlays?.[0]?.slug;
            item.onClick = this.handleOverlayClick(!item.selected ? overlays[0] : undefined);
        }

        // Handle expanded drilldown
        item.expanded = item.children.some(({ expanded, selected }) => Boolean(expanded) || Boolean(selected));

        if (!item.children.length && !item.onClick) return items;

        items.push(item);
        return items;
    }

    getItems() {
        return [
            {
                icon: 'corn',
                label: _('Maíz'),
                children: [
                    { label: _('Enfermedades'), type: 'corn_diseases_historic_pressure' },
                    { label: _('Plagas'), type: 'corn_pests_historic_pressure' },
                    { label: _('Caracteres Agrónomicos'), type: 'corn_others' },
                ],
            },
            { icon: 'weeds', label: _('Malezas'), type: 'weeds_rem' },
            { icon: 'alert', label: _('Pronóstico de Spodoptera'), type: 'spodoptera_forecast' },
            { icon: 'humidity', label: _('Humedad en el suelo'), type: 'humidity' },
        ];
    }

    getTree() {
        return this.getItems().reduce((items, item) => this.buildTree(items, item, 0), []);
    }

    handleOverlayClick(overlay) {
        return () => {
            const data = overlay && { type: overlay.slug, version: overlay.versions?.[0]?.versionNumber };
            if (this.props.versionLabel && overlay) {
                const sameLabelVersion = overlay.versions.find((v) => _(v.versionLabel) === this.props.versionLabel);
                if (sameLabelVersion) {
                    data.version = sameLabelVersion.versionNumber;
                } else {
                    this.props.setVersionLabel(_(overlay.versions?.[0]?.versionLabel));
                }
            }
            this.props.onOverlayClick(data);
        };
    }

    render() {
        const { field } = this.props;
        let tree = this.getTree();

        if (field) {
            // Solo se debe mostrar humidity en maps, y en maps no posee field
            tree = tree.filter((el) => el.type !== 'humidity');
        }

        return (
            <div className={styles.root}>
                <div className={styles.items}>
                    {tree.map(({ id, ...item }) => {
                        return <Item key={id} field={field} {...item} />;
                    })}
                </div>
            </div>
        );
    }
}
